import { useReducer, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { IconCheckmark, Spot, useNotification } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { AppLayout } from '@/shared/components/AppLayout'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { getBalance } from '@/features/user/api/balance'
import { formatCurrency } from '@/shared/helpers'
import { SLIM_ROUTES } from '@/shared/routes'
import { ContactStep } from '@/features/transfer/components/ContactStep'
import { AmountStep } from '@/features/transfer/components/AmountStep'
import { ReviewStep } from '@/features/transfer/components/ReviewStep'
import { SmsStep } from '@/features/transfer/components/SmsStep'
import { TRANSFER_CURRENCY } from '@/features/transfer/model/constants'
import { parseAmount } from '@/features/transfer/model/validation'
import { confirmPaymentOtp, initiatePayment } from '@/features/transfer/api/payment'
import {
  TRANSFER_FLOW,
  TRANSFER_STEPS,
  createTransferReducer,
  initialTransferState,
} from '@/features/transfer/model/transferReducer'

const reducer = createTransferReducer(TRANSFER_FLOW)

const TransferPage = () => {
  const { t, i18n } = useTranslation()
  useDocumentTitle(t('titles.transfer'))
  const navigate = useNavigate()
  const { notify } = useNotification()
  const [state, dispatch] = useReducer(
    reducer,
    TRANSFER_FLOW,
    initialTransferState
  )
  const [otp, setOtp] = useState('')
  const [paymentId, setPaymentId] = useState<string | null>(null)

  const { data: balance } = useQuery({
    queryKey: ['balance'],
    queryFn: getBalance,
  })
  const currency = balance?.availableAmount.currency ?? TRANSFER_CURRENCY
  const locale = i18n.resolvedLanguage
  const balanceLabel = balance
    ? formatCurrency({ value: balance.availableAmount.value, currency, locale })
    : '—'
  const amountLabel = formatCurrency({
    value: parseAmount(state.data.amount) || 0,
    currency,
    locale,
  })

  const accountName = t('transfer.accountName')
  const formTitle = t('transfer.formTitle')

  const goHome = () => navigate(SLIM_ROUTES.HOME)

  const next = (patch: Partial<typeof state.data>) => {
    dispatch({ type: 'UPDATE', patch })
    dispatch({ type: 'NEXT' })
  }

  const completeTransfer = () => {
    notify({
      icon: (
        <Spot color='teal'>
          <IconCheckmark />
        </Spot>
      ),
      title: t('transfer.toast.title'),
      description: t('transfer.toast.description', {
        amount: amountLabel,
        name: state.data.name,
      }),
    })
    goHome()
  }

  const initiate = useMutation({
    mutationFn: (pin: string) => initiatePayment(state.data, pin),
    onSuccess: ({ paymentId: id }) => {
      setPaymentId(id)
      dispatch({ type: 'NEXT' })
    },
  })

  const confirm = useMutation({
    // paymentId is always set before the SMS step is reachable: initiate's
    // onSuccess sets it and advances the step in the same call, and there's
    // no other way to reach TRANSFER_STEPS.SMS.
    mutationFn: (code: string) => confirmPaymentOtp(paymentId as string, code),
    onSuccess: completeTransfer,
  })

  return (
    <AppLayout>
      {state.step === TRANSFER_STEPS.CONTACT ? (
        <ContactStep
          title={formTitle}
          name={state.data.name}
          iban={state.data.iban}
          onBack={goHome}
          onContinue={next}
        />
      ) : null}

      {state.step === TRANSFER_STEPS.AMOUNT ? (
        <AmountStep
          title={formTitle}
          accountName={accountName}
          balanceLabel={balanceLabel}
          recipientName={state.data.name}
          recipientIban={state.data.iban}
          amount={state.data.amount}
          reference={state.data.reference}
          onBack={() => dispatch({ type: 'BACK' })}
          onContinue={next}
        />
      ) : null}

      {state.step === TRANSFER_STEPS.REVIEW ? (
        <ReviewStep
          title={t('transfer.reviewTitle')}
          data={state.data}
          accountName={accountName}
          amountLabel={amountLabel}
          onBack={() => dispatch({ type: 'BACK' })}
          onCancel={goHome}
          onConfirm={pin => initiate.mutate(pin)}
          isSubmitting={initiate.isPending}
          error={
            initiate.isError ? t('transfer.review.errors.generic') : undefined
          }
        />
      ) : null}

      {state.step === TRANSFER_STEPS.SMS ? (
        <SmsStep
          value={otp}
          onChange={setOtp}
          onBack={() => dispatch({ type: 'BACK' })}
          onCancel={goHome}
          onConfirm={() => confirm.mutate(otp)}
          isSubmitting={confirm.isPending}
          error={confirm.isError ? t('transfer.sms.errors.generic') : undefined}
        />
      ) : null}
    </AppLayout>
  )
}

export default TransferPage
